import { useState, useEffect, useCallback, useRef } from 'react';
import { listReportsForAdmin } from '../services/firestoreService';

export const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'approved', label: 'Approved' },
  { value: 'not_approved', label: 'Not approved' },
  { value: 'deleted', label: 'Deleted' },
  { value: 'pending', label: 'Pending (legacy)' },
];

export function statusFromAction(action) {
  if (action === 'approve') return 'approved';
  if (action === 'reject') return 'rejected';
  if (action === 'delete') return 'deleted';
  return null;
}

export function reportMatchesFilter(report, filters) {
  const { status = 'all' } = filters;
  const s = report.status;
  if (status === 'all') return true;
  if (status === 'approved') return s === 'approved';
  if (status === 'not_approved') return s === 'rejected' || s === 'pending';
  if (status === 'deleted') return s === 'deleted';
  return s === status;
}

export function applyModerationPatch(reports, reportIds, action, filters) {
  const newStatus = statusFromAction(action);
  if (!newStatus) return reports;

  const idSet = new Set(reportIds);
  const next = reports.map((r) =>
    idSet.has(r.id) ? { ...r, status: newStatus } : r
  );
  return next.filter((r) => reportMatchesFilter(r, filters));
}

export function useAdminReports(filters) {
  const [reports, setReports] = useState([]);
  const [initialLoading, setInitialLoading] = useState(true);
  const [tableLoading, setTableLoading] = useState(false);
  const [error, setError] = useState('');
  const hasLoadedRef = useRef(false);

  const fetchReports = useCallback(
    async ({ showTableLoading = true } = {}) => {
      setError('');
      if (showTableLoading) setTableLoading(true);

      try {
        const data = await listReportsForAdmin({
          status: filters.status,
          phoneNumberId: filters.phoneNumberId || undefined,
        });
        setReports(data);
      } catch (err) {
        setError(
          err.message +
            ' — deploy functions if you recently updated the admin API: firebase deploy --only functions'
        );
        setReports([]);
      } finally {
        setTableLoading(false);
        setInitialLoading(false);
        hasLoadedRef.current = true;
      }
    },
    [filters.status, filters.phoneNumberId]
  );

  useEffect(() => {
    const isFirst = !hasLoadedRef.current;
    fetchReports({ showTableLoading: !isFirst || true });
  }, [fetchReports]);

  const refresh = useCallback(() => {
    fetchReports({ showTableLoading: true });
  }, [fetchReports]);

  const patchReports = useCallback(
    (reportIds, action) => {
      setReports((prev) => applyModerationPatch(prev, reportIds, action, filters));
    },
    [filters]
  );

  const rollbackReports = useCallback((snapshot) => {
    setReports(snapshot);
  }, []);

  return {
    reports,
    initialLoading,
    tableLoading,
    error,
    setError,
    refresh,
    patchReports,
    rollbackReports,
  };
}
